<template>
  <swiper
    class="carousel-container slide_height"
    :loop="loop && slideItem.length > 1"
    :modules="modules"
    :centered-slides="centeredSlides"
    :navigation="navigation"
    :breakpoints="breakpoints"
    :speed="speed"
    :observer="observer"
    :observeParents="observeParents"
    :pagination="pagination"
    ref="mySwiper"
    @slideChange="onSlideChange"
    v-show="slideItem.length > 0"
  >
    <swiper-slide v-for="(item, index) in slideItem" :key="index" class="slide-item">
      <div class="slide_content">
        <!-- type = img -->
        <div class="slide_content img" v-if="item.type === 'image'">
          <router-link v-if="item.routerType === 'router'" :to="`/${currentLanguage}${item.routerLink}`" @click="onBannerClick(item)">
            <picture>
              <source media="(max-width: 990px)" :srcset="item.mobileSrc" v-if="item.mobileSrc" />
              <img :src="item.src" :alt="item.alt" />
            </picture>
          </router-link>
          <a v-else-if="item.routerType === 'external-link'" :href="item.routerLink" target="_blank">
            <picture>
              <source media="(max-width: 990px)" :srcset="item.mobileSrc" v-if="item.mobileSrc" />
              <img :src="item.src" :alt="item.alt" />
            </picture>
          </a>
          <div v-else>
            <picture>
              <source media="(max-width: 990px)" :srcset="item.mobileSrc" v-if="item.mobileSrc" />
              <img :src="item.src" :alt="item.alt" />
            </picture>
          </div>
        </div>
        <!-- type = yt-video -->
        <div class="slide_content yt-video" v-if="item.type === 'yt-video'">
          <img src="@/assets/img/products/Transparent_BG.png" alt="" />
          <div :id="'player-' + index" class="yt-video-iframe"></div>
        </div>
        <!-- type = video -->
        <div class="slide_content video" v-if="item.type === 'video'">
          <video :ref="'video-' + index" :src="item.src" muted playsinline webkit-playsinline draggable="true" :controls="!isMobile"></video>
        </div>
      </div>
    </swiper-slide>

    <div class="swiper-pagination"></div>
    <button type="button" class="swiper-button-prev" v-show="showNavButtons() && showDefaultNavButtons"></button>
    <button type="button" class="swiper-button-next" v-show="showNavButtons() && showDefaultNavButtons"></button>

    <!-- home arrow icon -->
    <button type="button" class="swiper-button-prev" v-show="!showDefaultNavButtons">
      <i class="bi bi-caret-left-fill"></i>
    </button>
    <button type="button" class="swiper-button-next" v-show="!showDefaultNavButtons">
      <i class="bi bi-caret-right-fill"></i>
    </button>
  </swiper>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import YouTubePlayer from 'youtube-player';
import { useGtmEventManager } from '@/utils/gtmEventManager';

export default {
  name: 'SwiperComponent',
  components: {
    Swiper,
    SwiperSlide,
  },
  setup() {
    return {
      modules: [Autoplay, Navigation, Pagination],
    };
  },
  data() {
    return {
      players: {},
      isInitialed: false,
      currentIndex: 0,
      autoPlayTimer: null,
      isUnmount: false,
    };
  },
  props: {
    showDefaultNavButtons: {
      type: Boolean,
      default: true,
    },
    slideItem: {
      type: Array,
      required: true,
    },
    loop: {
      type: Boolean,
      default: true,
    },
    centeredSlides: {
      type: Boolean,
      default: true,
    },
    navigation: {
      type: Object,
      default: () => ({
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
      }),
    },
    pagination: {
      type: [Object, Boolean],
      default: false,
    },
    breakpoints: {
      type: Object,
      default: () => ({
        576: { slidesPerView: 1.75, spaceBetween: 50 },
        0: { slidesPerView: 1.2, spaceBetween: 10 },
      }),
    },
    speed: {
      type: Number,
      default: 500,
    },
    observer: {
      type: Boolean,
      default: true,
    },
    observeParents: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    propPage: {
      type: String,
      default: '',
    },
  },
  watch: {
    slideItem: {
      handler(images) {
        this.isInitialed = false;

        this.$nextTick(() => {
          images.forEach((item, index) => {
            if (item.type === 'yt-video') {
              const uniqueKey = item.src || `video-${index}`;
              const player = YouTubePlayer(`player-${index}`, {
                videoId: uniqueKey,
                playerVars: {
                  autoplay: 0,
                  mute: 1,
                  rel: 0,
                  controls: this.isMobile ? 0 : 1,
                  start: item.clipStartTime || 0,
                  end: item.clipEndTime ? item.clipEndTime : undefined,
                },
              });

              this.players[`${uniqueKey}-${index}`] = player;

              player.on('stateChange', (event) => {
                if (event.data === 0) {
                  const swiper = document.querySelector('.swiper').swiper;
                  if (this.currentIndex === swiper.realIndex) {
                    swiper.slideNext();
                  }
                }
              });
            }
          });

          this.isInitialed = true;
          const swiper = document.querySelector('.swiper')?.swiper;
          if (swiper) {
            this.onSlideChange(swiper);
          }
        });
      },
      deep: true,
    },
  },
  beforeUnmount() {
    this.isUnmount = true;
    // prevent
    clearTimeout(this.autoPlayTimer);

    // clear yt player
    if (typeof this.players === 'object' && this.players !== null) {
      Object.keys(this.players).forEach((key) => {
        this.players[key].destroy();
      });
    }
    this.players = {};
  },
  methods: {
    showNavButtons() {
      return !this.isMobile && this.slideItem.length > 1;
    },
    getWrapperTag(type) {
      switch (type) {
        case 'router':
          return 'router-link';
        case 'external-link':
          return 'a';
        default:
          return 'div';
      }
    },
    onSlideChange(swiper) {
      if (!this.isInitialed || this.isUnmount || this.slideItem.length === 0) {
        return;
      }

      this.currentIndex = swiper.realIndex;
      clearTimeout(this.autoPlayTimer);

      const preIndex = this.currentIndex - 1 < 0 ? this.slideItem.length - 1 : this.currentIndex - 1;
      const nextIndex = this.currentIndex + 1 >= this.slideItem.length ? 0 : this.currentIndex + 1;
      const bannerPreType = this.slideItem[preIndex].type;
      const bannerNextType = this.slideItem[nextIndex].type;

      this.pauseVideoBanner(bannerPreType, this.slideItem[preIndex].src, preIndex);
      this.pauseVideoBanner(bannerNextType, this.slideItem[nextIndex].src, nextIndex);

      const bannerImg = this.slideItem[this.currentIndex];
      const bannerType = bannerImg.type;

      if (bannerType === 'yt-video') {
        const uniqueKey = bannerImg.src;
        const player = this.players[`${uniqueKey}-${this.currentIndex}`];
        if (player) {
          player.loadVideoById({
            videoId: uniqueKey,
            startSeconds: bannerImg.clipStartTime || 0, // 開始時間
            endSeconds: bannerImg.clipEndTime ? bannerImg.clipEndTime : undefined, // 結束時間
          });
          player.playVideo();
        }
      } else if (bannerType === 'image') {
        this.autoPlayTimer = setTimeout(() => {
          const swiper = document.querySelector('.swiper')?.swiper;
          if (swiper) {
            swiper.slideNext();
          }
        }, 5000);
      } else if (bannerType === 'video') {
        const videoEl = this.$refs[`video-${this.currentIndex}`];

        if (videoEl && videoEl[0]) {
          videoEl[0].currentTime = 0;
          videoEl[0].onended = () => {
            const swiper = document.querySelector('.swiper')?.swiper;
            swiper.slideNext();
          };

          setTimeout(() => {
            const videoEl = this.$refs[`video-${this.currentIndex}`];
            if (videoEl && videoEl[0]) {
              videoEl[0].play();
            }
          }, 500);
        }
      }
    },
    onBannerClick(item) {
      // gtm事件 點擊banner時觸發
      const { bannerClickGtmEvent } = useGtmEventManager();
      bannerClickGtmEvent(item.alt);
    },
    pauseVideoBanner(type, indexVideoId, currentIndex) {
      if (type === 'yt-video') {
        const uniqueKey = indexVideoId;
        const player = this.players[`${uniqueKey}-${currentIndex}`];
        player?.pauseVideo();
      } else if (type === 'video') {
        const videoEl = this.$refs[`video-${currentIndex}`];
        if (videoEl?.length) {
          videoEl[0].pause();
        }
      }
    },
  },
};
</script>

<style lang="scss">
.carousel-container {
  margin-top: 10px;
  background-color: #1e1e1e;
  .swiper-wrapper {
    align-items: center;
  }
  .slide_content {
    height: 100%;
    background-color: #181818;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .slide-item {
    height: 100%;
    opacity: 0.2;
    transition: all 0.3s;
  }

  .swiper-slide-active {
    opacity: 1;
    transform: scale(1);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .video {
    video {
      width: 100%;
      height: 100%;
    }
  }
  .yt-video {
    position: relative;
    height: 100%;
    overflow: hidden;

    .yt-video-iframe {
      width: 100%;
      height: 100%;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: auto auto;
      box-sizing: border-box;
    }

    .video iframe {
      display: block;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: auto auto;
    }
  }

  .swiper-button-prev,
  .swiper-button-next {
    color: rgba(255, 255, 255, 0.8);
  }
}
@media (max-width: 991px) {
  .carousel-container .swiper-button-prev {
    left: 0;
  }
  .carousel-container .swiper-button-next {
    right: 0;
  }
}
@media (max-width: 576px) {
  .productInfo-wrapper .carousel-container .yt-video .yt-video-iframe {
    pointer-events: none;
  }
}
</style>
